import { fetchUtils } from '$utils/fetchUtils'
import { get } from 'svelte/store'
import { t } from '$i18n/i18n'
import { addNotification } from '#lib/stores.js'
import { DefaultNotification, NotificationType, type Notification } from '$types/notification'
import { mmpvState } from '#lib/mmpv/mmpv.state.svelte.js'
import type { Material } from './types/material'
import { EmptyMaterial } from './types/material'
import type { MaterialProperty } from './types/materialProperty'
import type { Catalog } from './types/catalog'

type DeleteMaterialCallbacks = {
	addRoot: () => void
	changeMaterial: (material: Material) => void
}

/**
 * Deletes a material from the catalog via the API and updates catalog state.
 * Handles switching to the next appropriate material after deletion.
 */
export async function deleteMaterial(
	material: Material,
	catalog: Catalog,
	callbacks: DeleteMaterialCallbacks
): Promise<void> {
	await fetchUtils
		.post(`/api/catalog/${catalog.id}/material/${material.id}/delete`, material)
		.then(() => {
			const deletedMaterialId = material.id
			const indexOfDeletedNode = catalog.nodes.map((node) => node.id).indexOf(material.id)
			const parentNodeId = material.parentId

			if (indexOfDeletedNode > -1) {
				catalog.nodes.splice(indexOfDeletedNode, 1)
			}

			const indexOfParentNode = catalog.nodes.map((node) => node.id).indexOf(parentNodeId)
			if (indexOfParentNode > -1) {
				const indexToRemoveChildId =
					catalog.nodes[indexOfParentNode].childrenIds.indexOf(deletedMaterialId)
				if (indexToRemoveChildId > -1) {
					catalog.nodes[indexOfParentNode].childrenIds.splice(indexToRemoveChildId, 1)
				}
			}

			// Case 1: root node deleted, no remaining materials → create new empty root
			if (parentNodeId === 'root' && catalog.nodes.length === 0) {
				callbacks.addRoot()
				// Case 2: root node deleted, siblings remain → switch to first root sibling
			} else if (parentNodeId === 'root') {
				const indexNextRootMaterial = catalog.nodes.map((node) => node.parentId).indexOf('root')
				callbacks.changeMaterial(catalog.nodes[indexNextRootMaterial])
				// Case 3: non-root node deleted → switch to parent
			} else {
				callbacks.changeMaterial(catalog.nodes[indexOfParentNode])
			}

			const notification: Notification = DefaultNotification(
				get(t)('UI.mmpv.material.deleted.notification.success'),
				NotificationType.SUCCESS
			)
			addNotification(notification)
		})
		.catch(fetchUtils.catchErrorAndShowNotification())
}

/**
 * Saves or updates a material via the API and updates catalog.nodes accordingly.
 * Returns the updated material with its generated label on success, null on API failure.
 */
export async function saveOrUpdateMaterial(
	validatedMaterial: Material,
	catalog: Catalog
): Promise<Material | null> {
	/**
	 * normalize a cleared quantity to explicit null: JSON.stringify drops undefined, so an absent
	 * quantityId reaches the backend as "no change" and keeps the old assignment.
	 */
	const materialToPost: Material = {
		...validatedMaterial,
		quantityId: validatedMaterial.quantityId ?? null
	}
	const updatedMaterial: Material | null = await fetchUtils
		.post(`/api/catalog/${catalog.id}/material/${materialToPost.id}/update`, materialToPost)
		.then((data: Material) => {
			let indexOfOldMaterial = catalog.nodes
				.map((node: Material) => node.id)
				.indexOf(validatedMaterial.id)
			indexOfOldMaterial =
				indexOfOldMaterial > -1
					? indexOfOldMaterial
					: catalog.nodes.map((node: Material) => node.id).indexOf('new')
			const parentNodeId = validatedMaterial.parentId

			const generatedLabel =
				(data.parentCode ?? '') +
				data.code +
				(data.highlighted ? catalog.highlightedValue : '') +
				' ' +
				data.name
			const material = {
				...EmptyMaterial(),
				...data,
				label: generatedLabel,
				// an absent quantityId in the response means "no quantity assigned"
				quantityId: data.quantityId ?? null
			}

			if (indexOfOldMaterial > -1) {
				catalog.nodes.splice(indexOfOldMaterial, 1)
				catalog.nodes.push(material)
			}

			// replace 'new' childId with the generated id in the parent node
			if (parentNodeId && parentNodeId !== 'root') {
				const parentNode = mmpvState.findMaterial(parentNodeId)
				if (parentNode) {
					const indexOfOldChild = parentNode.childrenIds.indexOf('new')
					if (indexOfOldChild > -1) {
						parentNode.childrenIds.splice(indexOfOldChild, 1)
						parentNode.childrenIds.push(material.id)
					}
				}
			}
			return material
		})
		.catch((error: unknown): null => {
			fetchUtils.catchErrorAndShowNotification()(error)
			return null
		})

	if (updatedMaterial) {
		mmpvState.updateMaterialOnQuantity(updatedMaterial.quantityId, updatedMaterial.id)
	}
	return updatedMaterial
}

/**
 * Removes a property from a material via the API (for persisted materials) and
 * cleans up the propertyId from catalog.nodes.
 * Returns true if the caller should also update its local properties array.
 */
export async function removeMaterialProperty(
	material: Material,
	catalog: Catalog,
	propertyToRemove: MaterialProperty
): Promise<boolean> {
	const removeFromCatalogNodes = () => {
		const indexOfMaterialInCatalog = catalog.nodes
			.map((node: Material) => node.id)
			.indexOf(material.id)
		if (indexOfMaterialInCatalog > -1) {
			const catalogMaterial = catalog.nodes[indexOfMaterialInCatalog]
			const indexOfPropertyId = catalogMaterial.propertyIds.indexOf(propertyToRemove.id)
			if (indexOfPropertyId > -1) {
				catalogMaterial.propertyIds.splice(indexOfPropertyId, 1)
			}
			const indexOfProperty = catalogMaterial.properties.findIndex(
				(property) => property.id === propertyToRemove.id
			)
			if (indexOfProperty > -1) {
				catalogMaterial.properties.splice(indexOfProperty, 1)
			}
		}
	}

	if (material.id !== 'new') {
		let success = false
		await fetchUtils
			.get(`/api/material/${material.id}/property/${propertyToRemove.id}/remove`)
			.then(() => {
				removeFromCatalogNodes()
				success = true
			})
			.catch(fetchUtils.catchErrorAndShowNotification())
		return success
	}

	removeFromCatalogNodes()
	return true
}
